import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ContainerClient, SearchContainerMasksRequest } from '../../../core/openapi/bff-admin-api';
import { ToastService } from '@ui-lib/components';
import { catchError, exhaustMap, map, Observable, of, startWith, Subject, tap } from 'rxjs';

export interface ContainerMaskItem {
  id: string;
  name: string;
  mask: string[];
}

@Injectable()
export class ContainerSelectorService {
  private readonly containerClient = inject(ContainerClient);
  private readonly toastService = inject(ToastService);

  public readonly totalCount: WritableSignal<number> = signal<number>(0);
  public readonly isLoading: WritableSignal<boolean> = signal<boolean>(false);
  public readonly hasLoaded: WritableSignal<boolean> = signal<boolean>(false);

  private readonly batchSize = 100 as const;

  private request: Required<
    Pick<SearchContainerMasksRequest, 'skip' | 'take' | 'includeTotalCount'>
  > &
    Pick<SearchContainerMasksRequest, 'facilityId' | 'keyword'> = {
    skip: 0,
    take: this.batchSize,
    includeTotalCount: true,
    facilityId: undefined,
    keyword: null,
  };

  private readonly origin$: Subject<void> = new Subject<void>();

  private readonly _source$ = this.origin$.pipe(
    startWith(void 0),
    tap((): void => {
      this.isLoading.set(true);
      this.hasLoaded.set(false);
    }),
    exhaustMap(() =>
      this.containerClient.v1ContainerSearchMaskPost(this.request).pipe(
        tap(({ totalCount }): void => {
          this.totalCount.set(totalCount ?? 0);
        }),
        map(({ items }): ContainerMaskItem[] =>
          Array.isArray(items)
            ? items
                .filter((item): boolean => Boolean(item.containerId))
                .map(
                  (item): ContainerMaskItem => ({
                    id: item.containerId,
                    name: item.containerName,
                    mask: item.mask ?? [],
                  }),
                )
            : [],
        ),
        catchError((err: unknown): Observable<ContainerMaskItem[]> => {
          const failedSkip = this.request.skip;

          this.request = {
            ...this.request,
            skip: Math.max(failedSkip - this.batchSize, 0),
          };

          this.toastService.error({
            message: 'monitoring.selector.toast.models-fetching-error',
          });

          console.error('Error fetching container dictionary items:', err);

          return of([]);
        }),
      ),
    ),
    tap((): void => {
      this.isLoading.set(false);
      this.hasLoaded.set(true);
    }),
  );

  public source$(
    facilityId: string | null,
    keyword: string | null,
  ): Observable<ContainerMaskItem[]> {
    if (!facilityId) {
      this.totalCount.set(0);
      this.isLoading.set(false);
      this.hasLoaded.set(true);
      return of([]);
    }

    this.request = {
      ...this.request,
      skip: 0,
      facilityId,
      keyword: keyword && keyword.length > 0 ? keyword : null,
    };

    return this._source$;
  }

  public loadMore(): void {
    if (this.isLoading()) return;

    this.request = {
      ...this.request,
      skip: this.request.skip + this.batchSize,
    };

    this.origin$.next();
  }
}
